import { computed, inject, nextTick, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useGlobalStore } from '@/stores/global';
import { useCreateStore } from '@/stores/create';
import { useSignatureModeDraft } from '@/stores/create/signatureModeDraft';
import { useParticipantsDraft } from '@/stores/create/participantsDraft';
import { useDocumentWizard } from '@/composables/create/useDocumentWizard';
import { useOrganizationPageData } from '@/composables/useOrganizationData';
import { SignatureMode } from '@/types/enums/SignatureMode';
import { Jurisdiction } from '@/types/enums/Jurisdiction';
import { AuthorityService } from '@/types/enums/AuthorityService.ts';
import { orderBy } from '@/composables/useUtils';
import { EMAIL_REGEX } from '@deepcloud/field-validators';
import type { SigningWorkflowStep } from '@/types/create/WorkflowDraft';
import { PlaceHolderComposableKey } from '@/composables/placement/placementSymbols';
import {
  buildSigneeUpdatePayload,
  waitForDraggableToSettle
} from '@/composables/placement/usePlacementUtility';
import type { AxiosResponse } from 'axios';
import type { Signee } from '@/types/Signee';
import type { Placeholder } from '@/composables/placement/placementTypes';
import {
  getDefaultSigneeFileId,
  hasSigneePlacementOnFile,
  isPlaceholderForSignee
} from '@/utils/signeeSignings';
import { useSettingsDevStore } from '@/stores/settingsDev.ts';

export function useSigningWorkflow() {
  const globalStore = useGlobalStore();
  const createStore = useCreateStore();
  const modeDraft = useSignatureModeDraft();
  const participantsDraft = useParticipantsDraft();
  const settingsDevStore = useSettingsDevStore();
  const { signingWorkflowDialog, signingWorkflowStep, signingWorkflowWizard } =
    storeToRefs(globalStore);

  const { isWizardMode, completeWizard } = useDocumentWizard();

  const {
    signatureMode,
    jurisdiction,
    enforceAuthorityService,
    authorityService,
    isDirty: isModeDirty
  } = storeToRefs(modeDraft);

  const {
    signeeAttachmentsAllowed,
    canEnableAttachments,
    isDirty: isParticipantsDirty
  } = storeToRefs(participantsDraft);

  const isConfirming = ref(false);

  // The placement composable is provided by the document layout that hosts the
  // dialog. It may be absent in isolated usages, so inject non-strictly and
  // guard before using it.
  const placeholders = inject(PlaceHolderComposableKey, undefined);

  let availableModesQuery: ReturnType<
    typeof useOrganizationPageData
  >['availableModesQuery'];

  try {
    ({ availableModesQuery } = useOrganizationPageData());
  } catch {
    availableModesQuery = undefined;
  }

  const isOpen = computed({
    get: () => signingWorkflowDialog.value,
    set: (value: boolean) => {
      signingWorkflowDialog.value = value;
    }
  });

  const step = computed({
    get: () => signingWorkflowStep.value,
    set: (value: SigningWorkflowStep) => {
      signingWorkflowStep.value = value;
    }
  });

  const isWizard = computed({
    get: () => signingWorkflowWizard.value,
    set: (value: boolean) => {
      signingWorkflowWizard.value = value;
    }
  });

  const isDirty = computed(
    () => isModeDirty.value || isParticipantsDirty.value
  );

  const canEnforceAuthorityService = computed(() => {
    if (signatureMode.value === SignatureMode.TIMESTAMP) return false;
    if (
      signatureMode.value === SignatureMode.ADVANCED &&
      jurisdiction.value === Jurisdiction.EIDAS
    ) {
      return false;
    }
    return true;
  });

  const availableAuthorityServices = computed(
    (): { key: AuthorityService }[] => {
      const modes = availableModesQuery?.data.value;
      if (!modes) return [];

      const bySignature = modes[signatureMode.value];
      if (!bySignature) return [];

      const services = bySignature[jurisdiction.value];
      if (!services || services.length === 0) return [];
      const orderedServices = orderBy(
        services.map((mode: AuthorityService) => ({ key: mode })),
        ['key']
      );
      return settingsDevStore.canUseDevFeature('DEEPSIGN_SIGN8')
        ? orderedServices
        : orderedServices.filter(
            (mode) => String(mode.key) !== AuthorityService.SIGN8
          );
    }
  );

  function init() {
    modeDraft.initFromDocument();
    participantsDraft.initFromDocument();
    // Clone the participant/sign-order state into a draft so every edit in the
    // dialog stays local until the user clicks "Confirm"; the document remains
    // the committed source of truth.
    createStore.deferPersistence = true;
    createStore.beginParticipantsDraft();
    // Drop any quorum edits left over from a previous (cancelled) session.
    createStore.clearGroupQuorumDraft();
  }

  // First-time setup: full stepper wizard (Signature Mode → Participants).
  function openWizard() {
    init();
    isWizard.value = true;
    step.value = 1;
    isOpen.value = true;
  }

  // Single-section edit (no stepper) opened to a specific step.
  function open(startStep: SigningWorkflowStep = 1) {
    init();
    isWizard.value = false;
    step.value = startStep;
    isOpen.value = true;
  }

  // Summary cards: open the wizard while it's still pending, otherwise just the
  // clicked section.
  function openFromCard(targetStep: SigningWorkflowStep) {
    const documentId = createStore.document?.documentId;
    if (documentId && isWizardMode(documentId)) {
      openWizard();
    } else {
      open(targetStep);
    }
  }

  function close() {
    isOpen.value = false;
  }

  function goToStep(targetStep: SigningWorkflowStep) {
    step.value = targetStep;
  }

  function goNext() {
    if (step.value === 1) {
      step.value = 2;
    }
  }

  function goBack() {
    if (step.value === 2) {
      step.value = 1;
    }
  }

  function cancel() {
    // The stepper wizard is optional, so dismissing it (Close / Esc /
    // click-outside) counts as completing setup: flip the per-document flag so
    // it won't auto-open again. Confirming does the same in confirm(). Only for
    // the full wizard — single-section edits must not mark setup complete.
    if (isWizard.value) {
      const documentId = createStore.document?.documentId;
      if (documentId) completeWizard(documentId);
    }
    modeDraft.discard();
    createStore.discardParticipantsDraft();
    // Quorum edits live outside the participants draft and getGroupQuorum
    // reads them regardless of deferPersistence, so a cancelled quorum would
    // otherwise keep showing on the committed surfaces (the drawer's summary
    // card, InvitationsDialog) until the dialog is reopened and init() clears
    // it.
    createStore.clearGroupQuorumDraft();
    createStore.deferPersistence = false;
    close();
  }

  // Blocks confirm only on a genuinely malformed (non-empty, invalid) email —
  // an empty field is a deliberately prepared-for-later row, not an error (see
  // participantsDraft.validate()), so it's never focused here.
  async function validateParticipants(): Promise<boolean> {
    const validation = participantsDraft.validate();
    if (!validation.valid) {
      const invalidSignee = createStore.signeesOrderedFlat.find(
        (signee) => signee.email && !EMAIL_REGEX.test(signee.email)
      );
      if (invalidSignee) {
        await createStore.focusInputField(invalidSignee.signeeId);
      }
      return false;
    }

    const form = createStore.inviteeFormTemplateRef as {
      validate?: () => Promise<{ valid: boolean }>;
    } | null;

    if (form?.validate) {
      const result = await form.validate();
      if (!result.valid) {
        return false;
      }
    }

    return true;
  }

  async function confirm() {
    // Validate only the section the dialog is actually editing. The full wizard
    // sets up both, so it validates both; a single-section edit validates just
    // its own step — otherwise confirming a Signature-mode edit would be blocked
    // by unrelated participant issues (e.g. no signees yet). reconcile* for the
    // untouched section is a no-op (the draft equals the committed document).
    const validatesMode = isWizard.value || step.value === 1;
    const validatesParticipants = isWizard.value || step.value === 2;

    if (validatesMode && !modeDraft.validate().valid) {
      return false;
    }
    if (validatesParticipants && !(await validateParticipants())) {
      return false;
    }

    isConfirming.value = true;
    try {
      // Read before reconcile commits the draft: until then the flag still
      // describes the document as it was when the dialog opened (the switch
      // that maintains it reads the committed participants, never the draft),
      // which is exactly the "auto-place was ON" intent we need here.
      const shouldAutoPlaceNewSignees = createStore.placeSignaturesModel;
      // Persist all the buffered changes in one batch.
      const { addedSigneeIds, removedSigneeIds } =
        await createStore.reconcileParticipants();
      // Quorum after participants: the groups now exist server-side, so their
      // signeeGroupIds are resolvable. A membership change has to be reported:
      // without a sign order it is the only signal that a quorum may no longer
      // fit its group's member count (see reconcileGroupQuorums).
      await createStore.reconcileGroupQuorums(
        (addedSigneeIds?.length ?? 0) > 0 || (removedSigneeIds?.length ?? 0) > 0
      );
      await participantsDraft.reconcileAttachments();
      await modeDraft.reconcile();
      // Only leave deferred mode once everything persisted; on error the dialog
      // stays open so the user can retry or cancel.
      createStore.deferPersistence = false;
      // Reconcile placeholders against the confirmed participant changes: drop
      // placeholders for removed signees and, with auto-placement enabled, add
      // one on the selected file for each newly added signee. These run only
      // after the confirmation succeeded and are awaited so the dialog stays in
      // its confirming state until the placeholders reflect the change.
      removePlaceholdersForRemovedSignees(removedSigneeIds ?? []);
      if (shouldAutoPlaceNewSignees) {
        await autoPlaceNewSignees(addedSigneeIds ?? []);
      }
      // Completing the wizard flips the per-document flag so future edits open
      // each section on its own instead of the stepper.
      if (isWizard.value) {
        const documentId = createStore.document?.documentId;
        if (documentId) {
          completeWizard(documentId);
        }
      }
      close();
      return true;
    } catch (error) {
      console.error('Failed to confirm signing workflow', error);
      return false;
    } finally {
      isConfirming.value = false;
    }
  }

  // Drop every placeholder that belonged to a signee removed during this
  // confirmation so the canvas no longer renders orphaned placeholders. The
  // backend signee (and its positions) is already deleted by reconcile.
  function removePlaceholdersForRemovedSignees(removedSigneeIds: string[]) {
    if (!placeholders || removedSigneeIds.length === 0) return;
    const removed = new Set(removedSigneeIds);
    const list = placeholders.currentPlaceholders.value as Placeholder[];
    for (let index = list.length - 1; index >= 0; index -= 1) {
      if (removed.has(list[index].id)) {
        list.splice(index, 1);
      }
    }
  }

  // Auto-place a signature placeholder on the currently selected file for each
  // freshly added signee that has none there yet. Caller gates on the
  // pre-reconcile place-signatures intent (completeness becomes false as soon
  // as new signees exist). Scoped to the selected file (envelope-aware): the
  // persisted payload only touches that file so other files are preserved.
  // Awaitable and never throws so a placement failure can't roll back the
  // already-committed confirmation.
  async function autoPlaceNewSignees(addedSigneeIds: string[]): Promise<void> {
    if (!placeholders || addedSigneeIds.length === 0) {
      return;
    }

    const {
      placePlaceholdersAutomatically,
      placePlaceholdersByAutographData,
      placeholderTemplateRefs,
      currentPlaceholders,
      requestPlaceholderRemount
    } = placeholders;

    // Held for the whole placement so the drawer's PlaceSignaturesSwitch keeps
    // its ON state: the signees exist (committed by reconcile) but their
    // placeholders are only persisted at the end of this function, so per-file
    // completeness reads false in between.
    createStore.isAutoPlacingNewSignees = true;
    try {
      // Defer so the committed document propagates and the placement watcher has
      // rebuilt (and rendered) the new signees' placeholders before positioning.
      await new Promise((resolve) => setTimeout(resolve, 1000));
      await nextTick();
      const selectedFileId =
        globalStore.selectedDocumentFileId ??
        getDefaultSigneeFileId(createStore.document);

      const signeesToPlace = createStore.filteredSignees.filter(
        (signee) =>
          addedSigneeIds.includes(signee.signeeId) &&
          !hasSigneePlacementOnFile(
            signee,
            selectedFileId,
            createStore.document
          )
      );
      if (signeesToPlace.length === 0) return;

      const placedSigneeIds = signeesToPlace.map((signee) => signee.signeeId);
      await placePlaceholdersAutomatically(signeesToPlace);
      await nextTick();

      // Persist directly (mirroring the drag-and-drop flow) instead of going
      // through the policy-gated persistSigneePlaceholders: a freshly added
      // signee has no signings yet and a false signee-level policy, so the gate
      // would strip the autographPosition/fileAutographPositions and send an
      // empty payload. In the draft/create flow the creator may always place.
      for (const signee of signeesToPlace) {
        const payload = await buildSigneeUpdatePayload(
          signee,
          currentPlaceholders.value as Placeholder[],
          placeholderTemplateRefs?.value,
          createStore.document,
          selectedFileId
        );
        const response = (await createStore.updateInvitee(
          createStore.document!.documentId,
          signee.signeeId,
          payload
        )) as AxiosResponse<Signee>;
        if (response?.data) {
          createStore.updateInviteeInLocalData(response.data);
        }
      }

      // Let the rebuild triggered by updateInviteeInLocalData settle. That
      // rebuild reconstructs the placeholder from the persisted `signings`, which
      // yields an autograph-driven placeholder that is initially invisible
      // (exactly like right after a page load). Make those placeholders visible
      // at their persisted positions the same way a page load / file switch does
      // — via placePlaceholdersByAutographData — instead of relying on the
      // rebuild to preserve the transient in-memory visible slot (it is cleared
      // while participants reconcile).
      await nextTick();
      placePlaceholdersByAutographData();
      await nextTick();
      await nextTick();

      // Remount the newly placed placeholders so the draggable library
      // (re)initialises their drag/resize handling. Target them by the placed
      // signees (their uids may have changed in the rebuild), leaving
      // already-placed placeholders untouched so they don't shift.
      const uidsToRemount = (currentPlaceholders.value as Placeholder[])
        .filter(
          (placeholder) =>
            placedSigneeIds.some((signeeId) =>
              isPlaceholderForSignee(
                placeholder,
                signeeId,
                createStore.document?.signeeGroups
              )
            ) && Boolean(placeholder.autographPosition)
        )
        .map((placeholder) => placeholder.uid)
        .filter((uid): uid is string => Boolean(uid));
      await waitForDraggableToSettle();
      requestPlaceholderRemount(uidsToRemount);
    } catch (error) {
      console.error('Failed to auto-place new signees', error);
    } finally {
      createStore.isAutoPlacingNewSignees = false;
    }
  }

  async function refetchAvailableModes() {
    try {
      await availableModesQuery?.refetch();
    } catch (error) {
      console.error('Error refetching available modes:', error);
    }
  }

  return {
    isOpen,
    step,
    isDirty,
    isConfirming,

    signatureMode,
    jurisdiction,
    enforceAuthorityService,
    authorityService,
    setSignatureMode: modeDraft.setSignatureMode,
    setJurisdiction: modeDraft.setJurisdiction,
    setEnforceAuthorityService: modeDraft.setEnforceAuthorityService,
    setAuthorityService: modeDraft.setAuthorityService,
    canEnforceAuthorityService,
    availableAuthorityServices,
    refetchAvailableModes,

    signeeAttachmentsAllowed,
    canEnableAttachments,
    setSigneeAttachmentsAllowed: participantsDraft.setSigneeAttachmentsAllowed,

    isWizard,
    init,
    open,
    openWizard,
    openFromCard,
    close,
    goToStep,
    goNext,
    goBack,
    cancel,
    confirm
  };
}
